import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import {
  Button,
  Row,
  Col,
  Card,
  List,
  Avatar,
  Tag,
  Modal,
  Input,
  Typography,
  message,
  Space,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  UserAddOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

export default function TeamMembers({
  teamLead,
  teamMembers,
  isLoading,
  fetchTeamData,
  roomChat,
}) {
  const { t } = useTranslation();
  const tt = t("teamTab.membersPage", { returnObjects: true });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [teamName, setTeamName] = useState("");
  const [isPaying, setIsPaying] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const isShopifyUser = JSON.parse(localStorage.getItem('Userdata') || '{}')?.mode === 'shopify';
  const isInvited = localStorage.getItem("invited") === "true";
  const [LoadingRedirection, setLoadingRedirection] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchTeamData();
    checkPaymentStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${config.baseUrl}/administration/team/stripe/subscription-status/`, {
        headers: { Authorization: `Token ${token}` },
      });
      setSubscriptionActive(response.data.active || response.data?.status === "trialing");
    } catch (error) {
      console.error(tt.portalError, error);
    }
  };

  const checkPaymentStatus = async () => {
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      try {
        const response = await axios.post(
          `${config.baseUrl}/administration/team/stripe/verify-payment/`,
          { session_id: sessionId },
          { headers: { Authorization: `Token ${token}` } }
        );

        if (response.data.success) {
          message.success(tt.paymentVerified);
          setSubscriptionActive(true);
          navigate(location.pathname, { replace: true });
        }
      } catch {
        message.error(tt.paymentFailed);
      }
    }
  };

  const removeUser = async (userId) => {
    try {
      message.loading({ content: tt.removing, key: "remove" });
      const response = await axios.delete(`${config.baseUrl}/administration/team?email=${userId}`, {
        headers: { Authorization: `Token ${token}` },
      });

      if (response.data.success) {
        message.success({ content: tt.removeSuccess, key: "remove", duration: 2 });
        fetchTeamData();
      } else {
        message.error(response.data.error || tt.addFailed);
      }
    } catch {
      message.error(tt.removeError);
    }
  };

  const handlePayment = async () => {
    try {
      setIsPaying(true);
      if (isShopifyUser) {
        const res = await axios.post(`${config.baseUrl}/administration/team/shopify/billing/`,
          { action: 'invite_member', team_name: teamName, email },
          { headers: { Authorization: `Token ${token}` } });
        window.location.href = res.data.url;
      } else {
        const res = await axios.post(`${config.baseUrl}/administration/team/stripe/create-checkout-session/`, {}, {
          headers: { Authorization: `Token ${token}` },
        });
        window.location.href = res.data.url;
      }
    } catch (error) {
      message.error(isShopifyUser ? tt.billingFailed : tt.stripeFailed);
    } finally {
      setIsPaying(false);
    }
  };

  const addMember = async () => {
    const key = 'updatable';
    try {
      message.loading({ content: tt.addingMember, key });
      const response = await axios.post(`${config.baseUrl}/administration/team/`,
        { email, team_name: teamName },
        { headers: { Authorization: `Token ${token}` } });

      if (response.data.message === "Invitation sent successfully") {
        message.success({ content: tt.addSuccess, key, duration: 2 });
        fetchTeamData();
      } else {
        message.error(response.data.error || tt.addFailed);
      }
    } catch (error) {
      message.error(error.response?.data?.error || tt.addFailed);
    }
  };

  const createCustomerPortal = async () => {
    setLoadingRedirection(true);
    try {
      const response = await axios.post(`${config.apiUrl}stripe/create-customer-portal-session/`, {}, {
        headers: { Authorization: `Token ${token}` },
      });
      window.location.href = response.data.url;
    } catch (error) {
      message.error(tt.portalError);
    } finally {
      setLoadingRedirection(false);
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      {!isInvited && (
        <Card style={{ marginBottom: "24px", backgroundColor: subscriptionActive ? "#f6ffed" : "#fff2f0" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                {subscriptionActive ? (
                  <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                ) : (
                  <ExclamationCircleOutlined style={{ color: "#ff4d4f", fontSize: "20px" }} />
                )}
                <Title level={5}>{subscriptionActive ? tt.subscriptionActive : tt.subscriptionInactive}</Title>
              </Space>
            </Col>
            <Col>
              <Button type="primary" onClick={subscriptionActive ? createCustomerPortal : handlePayment} loading={isPaying || LoadingRedirection}>
                {subscriptionActive ? tt.manageSubscription : tt.subscribeNow}
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      <div className="flex justify-between items-center mb-4">
        <Title level={3}>{tt.title}</Title>
        {!isInvited && (
          <Tooltip title={subscriptionActive ? tt.inviteTooltip : tt.inviteTooltipLocked}>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => setIsModalOpen(true)}
              disabled={!subscriptionActive}
              style={{ backgroundColor: subscriptionActive ? "#7F28F8" : "#d9d9d9" }}
            >
              {tt.inviteMember}
            </Button>
          </Tooltip>
        )}
      </div>

      <Card>
        <List
          itemLayout="horizontal"
          loading={isLoading}
          dataSource={teamMembers}
          renderItem={(member) => (
            <List.Item actions={!isInvited ? [
              <Popconfirm
                title={tt.removeUserConfirm}
                onConfirm={() => removeUser(member?.id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title={tt.removeUser}>
                  <Button danger shape="circle" icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>,
            ] : []}>
              <List.Item.Meta
                avatar={<Avatar size={40} src={member.user?.image}>{!member.user?.image && '👤'}</Avatar>}
                title={
                  <Space>
                    <Text>{member.user?.email || member.invitation?.[0]?.invitee_email || tt.inviteeEmail}</Text>
                    <Tag color={member.invitation?.[0]?.status === 'accepted' ? 'green' : 'orange'}>
                      {member.invitation?.[0]?.status === 'accepted' ? tt.accepted : tt.pending}
                    </Tag>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      {teamLead && (
        <div style={{ marginTop: "24px" }}>
          <Title level={4}><CrownOutlined style={{ color: "#FFD700", marginRight: "8px" }} />{tt.leadTag}</Title>
          <div className="flex items-center gap-3">
            <Avatar size={64} src={teamLead.image}>
              {!teamLead.image && '👑'}
            </Avatar>
            <Text>{teamLead.email}</Text>
          </div>
        </div>
      )}

      <Modal
        title={tt.inviteMember}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>{tt.cancel}</Button>,
          <Button key="submit" type="primary" onClick={addMember}>{tt.invite}</Button>,
        ]}
      >
        {!teamMembers.length && (
          <Input
            placeholder={tt.enterTeamName}
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            className="mb-4"
          />
        )}
        <Input
          placeholder={tt.enterEmail}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Modal>
    </div>
  );
}
